import { RootState } from '../../../../../../../../init-store';
import { selectRoomAgentCanToggleVideo } from '../../../../../../../shared/features/config/selectors';
import {
    addDTMFCommandPrefix,
    DTMF_COMMANDS,
} from '../../../../../../../shared/features/room-agent/dtmf/dtmf-commands';
import {
    selectIsSelfAudioMuted,
    selectIsSelfVideoMuted,
} from '../../../../../../../shared/features/room-agent/dtmf/selectors';
import { i18n } from '../../../../../../../shared/helpers/i18n';
import { GRID_VIEW } from '../../../../../constants';
import { getActiveGridView, selectSelfReactionType } from '../../../../../selectors';
import { FONT_FAMILY, TEXT_COLOR_PRIMARY } from '../constants';
import { drawDTMFChar, DTMFCharColor } from '../dtmf-char';
import { Area, Drawer } from '../types';
import { SCALE } from './constants';

type CommandDescriptor = {
    getText: (state: RootState) => string;
    command: string[];
};

function getCommandList(state: RootState): CommandDescriptor[] {
    const list: CommandDescriptor[] = [
        {
            getText(state) {
                if (selectIsSelfAudioMuted(state)) {
                    return i18n('dtmf', 'command-list.audio.unmute');
                } else {
                    return i18n('dtmf', 'command-list.audio.mute');
                }
            },
            command: addDTMFCommandPrefix(DTMF_COMMANDS.muteAudio.toggle).split(''),
        },
    ];

    if (selectRoomAgentCanToggleVideo(state)) {
        list.push({
            getText(state) {
                if (selectIsSelfVideoMuted(state)) {
                    return i18n('dtmf', 'command-list.video.unmute');
                } else {
                    return i18n('dtmf', 'command-list.video.mute');
                }
            },
            command: addDTMFCommandPrefix(DTMF_COMMANDS.muteVideo.toggle).split(''),
        });
    }

    list.push(
        {
            getText(state) {
                if (selectSelfReactionType(state) === 'hand') {
                    return i18n('dtmf', 'command-list.reactions.hand-down');
                } else {
                    return i18n('dtmf', 'command-list.reactions.hand-up');
                }
            },
            command: addDTMFCommandPrefix(DTMF_COMMANDS.hand.toggle).split(''),
        },
        {
            getText(state) {
                const activeGridView = getActiveGridView(state);
                const isPresenter = activeGridView === GRID_VIEW.PRESENTER;
                if (isPresenter) {
                    return i18n('dtmf', 'command-list.gallery-view.turn-on');
                } else {
                    return i18n('dtmf', 'command-list.presenter-view.turn-on');
                }
            },
            command: addDTMFCommandPrefix(DTMF_COMMANDS.gridView.toggle).split(''),
        },
        {
            getText() {
                return i18n('dtmf', 'command-list.participant-list.show');
            },
            command: addDTMFCommandPrefix(DTMF_COMMANDS.participantList.open).split(''),
        },
        {
            getText() {
                return i18n('dtmf', 'pagination-prev-page');
            },
            command: DTMF_COMMANDS.pagination.prevPage.split(''),
        },
        {
            getText() {
                return i18n('dtmf', 'pagination-next-page');
            },
            command: DTMF_COMMANDS.pagination.nextPage.split(''),
        }
    );

    return list;
}

const COMMAND_MESSAGE_FONT_SIZE = Math.round(18 * SCALE);
const COMMAND_MESSAGE_FONT = `${COMMAND_MESSAGE_FONT_SIZE}px ${FONT_FAMILY}`;
const COMMAND_ICON_SIZE = Math.round(28 * SCALE);
const COMMAND_ICON_MARGIN = Math.round(8 * SCALE);
const COMMAND_ITEM_HEIGHT = COMMAND_ICON_SIZE;
const COMMAND_ITEM_MARGIN_BOTTOM = Math.round(SCALE * 15);

export function getCommandsHeight(state: RootState): number {
    const listLength = getCommandList(state).length;
    if (listLength <= 0) {
        return 0;
    }
    return COMMAND_ITEM_HEIGHT * listLength + COMMAND_ITEM_MARGIN_BOTTOM * (listLength - 1);
}

export class CommandsDrawer implements Drawer {
    private area: Area;
    private textOffsetY?: number;

    constructor(area: Area) {
        this.area = area;
    }

    private getTextOffsetY(ctx: CanvasRenderingContext2D): number {
        if (!this.textOffsetY) {
            ctx.textBaseline = 'top';
            ctx.font = COMMAND_MESSAGE_FONT;

            const textMetrics = ctx.measureText('T');
            const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;

            this.textOffsetY = Math.round((COMMAND_ITEM_HEIGHT - textHeight) / 2);
        }

        return this.textOffsetY;
    }

    draw(ctx: CanvasRenderingContext2D, state: RootState) {
        const list = getCommandList(state);
        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            const iconY = this.area.y + i * (COMMAND_ITEM_HEIGHT + COMMAND_ITEM_MARGIN_BOTTOM);
            const textY = iconY + this.getTextOffsetY(ctx);

            ctx.textBaseline = 'top';
            ctx.font = COMMAND_MESSAGE_FONT;
            ctx.fillStyle = TEXT_COLOR_PRIMARY;
            ctx.fillText(item.getText(state), this.area.x, textY);

            for (let j = 0; j < item.command.length; j++) {
                const offsetMultiplier = item.command.length - j;

                drawDTMFChar({
                    ctx,
                    x:
                        this.area.x +
                        this.area.width -
                        (offsetMultiplier * COMMAND_ICON_SIZE + (offsetMultiplier - 1) * COMMAND_ICON_MARGIN),
                    y: iconY,
                    char: item.command[j],
                    color: DTMFCharColor.default,
                    size: COMMAND_ICON_SIZE,
                });
            }
        }
    }
}
